import { OfficeCitiesMappingPropsEntity, RegionDetailsEntity } from '../../../interface/offices'
import { useEffect, useState, useMemo } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { generateOfficeColor } from '../../../utils/shares';

const OfficeCoverageOverviewFeatureLayer = ({
    assignments,
    initialSelectedCities = [],
    isShowTerritory = false,
    selectedOfficeSet = new Set<string>()
}: OfficeCitiesMappingPropsEntity) => {
    const map = useMap();
    const [selectedAreas, setSelectedAreas] = useState<RegionDetailsEntity[]>(initialSelectedCities);
    const [localAssignments, setLocalAssignments] = useState<RegionDetailsEntity[]>(assignments);

    useEffect(() => {
        setSelectedAreas(initialSelectedCities ?? []);
    }, [initialSelectedCities]);

    useEffect(() => {
        setLocalAssignments([...assignments]);
    }, [assignments]);

    const selectedUnassignedCities = useMemo(() => {
        const placeIds: string[] = [];

        selectedAreas.forEach((place) => {
            if (place.type === 'COUNTRY' && place.country_providerId) {
                placeIds.push(place.country_providerId);
            } else if (place.type === 'ADMINISTRATIVE_AREA_LEVEL_1' && place.state_providerId) {
                placeIds.push(place.state_providerId);
            } else if (place.type === 'ADMINISTRATIVE_AREA_LEVEL_2' && place.division_providerId) {
                placeIds.push(place.division_providerId);
            } else if (place.city_providerId) {
                placeIds.push(place.city_providerId);
            }
        });

        return placeIds;
    }, [selectedAreas]);

    const assignmentMaps = useMemo(() => {
        const city = new Map<string, RegionDetailsEntity>();
        const division = new Map<string, RegionDetailsEntity>();
        const state = new Map<string, RegionDetailsEntity>();
        const country = new Map<string, RegionDetailsEntity>();

        localAssignments.forEach(a => {
            if (a.city_providerId) city.set(a.city_providerId, a);
            else if (a.division_providerId) division.set(a.division_providerId, a);
            else if (a.state_providerId) state.set(a.state_providerId, a);
            else if (a.country_providerId) country.set(a.country_providerId, a);
        });

        return { city, division, state, country };
    }, [localAssignments]);

    const officeColorMap = useMemo(() => {
        const colors = new Map<string, ReturnType<typeof generateOfficeColor>>();

        localAssignments.forEach(a => {
            if (a.officeId && !colors.has(a.officeId)) {
                colors.set(a.officeId, generateOfficeColor(a.officeId));
            }
        });

        return colors;
    }, [localAssignments]);


    useEffect(() => {
        if (!map) return;

        // Apply styling to all feature types
        const featureTypes = ["LOCALITY", "ADMINISTRATIVE_AREA_LEVEL_1", 'ADMINISTRATIVE_AREA_LEVEL_2', "COUNTRY"];

        featureTypes.forEach((featureType) => {
            const featureLayer = map.getFeatureLayer(featureType as google.maps.FeatureType);

            featureLayer.style = (feature) => {
                if (!isShowTerritory) {
                    return { fillOpacity: 0, strokeOpacity: 0 };
                }

                const placeId = (feature.feature as any)?.placeId;

                let assignment;
                if (featureType === 'LOCALITY') {
                    assignment = assignmentMaps.city.get(placeId);
                } else if (featureType === 'ADMINISTRATIVE_AREA_LEVEL_2') {
                    assignment = assignmentMaps.division.get(placeId);
                } else if (featureType === 'ADMINISTRATIVE_AREA_LEVEL_1') {
                    assignment = assignmentMaps.state.get(placeId);
                } else if (featureType === 'COUNTRY') {
                    assignment = assignmentMaps.country.get(placeId);
                }

                // Office-assigned cities and administrative areas
                if (assignment) {
                    const officeColor = officeColorMap.get(assignment.officeId);

                    // Only show office colors for OTHER offices.
                    // If it's assigned to the current office and the user unselects it,
                    // it should go back to the default styling (not stay colored).
                    if (officeColor) {
                        if (selectedOfficeSet.has(assignment.officeId)) {
                            return {
                                fillColor: officeColor.fill,
                                fillOpacity: 0.6,
                                strokeColor: officeColor.stroke,
                                strokeWeight: 2
                            };
                        }
                    }
                }

            };
        });

        return () => {

        };
    }, [map, assignmentMaps, isShowTerritory, officeColorMap, selectedOfficeSet, selectedUnassignedCities]);

    return null;
};

export default OfficeCoverageOverviewFeatureLayer;
